import React, { useMemo, useRef, useCallback } from "react";
import clsx from "clsx";
import CustomTooltip from "app/components/Tooltip";
import SortIcon from "app/components/Icons/SortIcon";
import { sortOrder } from "app/types";
import CircularLoader from "app/components/CircularLoader";
import { TableHeader } from "app/components/Table/types";

interface CommonGridTableProps {
  headers: TableHeader[];
  orderBy?: string;
  orderDir?: sortOrder;
  onSort?: (headerId: string) => void;
  emptyText?: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  className?: string;
  headerClassName?: string;
  children?: React.ReactNode;
  gridTemplateColumns?: string;
  headerTitleClassName?: string;
  padding?: string;
}

const CommonGridTable: React.FC<CommonGridTableProps> = ({
  headers,
  orderBy,
  orderDir,
  onSort,
  emptyText = "No data available",
  isLoading = false,
  onLoadMore,
  hasMore = false,
  className,
  children,
  gridTemplateColumns,
  headerTitleClassName,
  headerClassName,
  padding,
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);

  // Memoize grid template for performance
  const gridTemplate = useMemo(() => {
    return `repeat(${headers.length}, 1fr)`;
  }, [headers]);

  // UseCallback ensures handleScroll isn't recreated each render
  const handleScroll = useCallback(() => {
    if (!divRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = divRef.current;
    // Trigger load more when user scrolls near bottom
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onLoadMore?.();
    }
  }, [isLoading, hasMore, onLoadMore]);

  return (
    <div
      ref={divRef}
      onScroll={handleScroll}
      className={clsx("w-full relative overflow-auto", className)}
    >
      {/* ===== Header ===== */}
      <div className="sticky top-0 z-30 bg-gray-400">
        <div className={clsx("px-6", padding)}>
          <div
            className={clsx(
              "grid items-center text-[12px] min-h-[52px] h-[52px] border border-b-0  border-gray-100 rounded-t-md   ",
              headerClassName,
            )}
            style={{
              gridTemplateColumns: gridTemplateColumns
                ? gridTemplateColumns
                : gridTemplate,
            }}
          >
            {headers.map((header) => (
              <div
                key={header.id}
                className={clsx(
                  "relative select-none hover:bg-gray-100 h-full group border-b border-gray-100 flex items-center px-4 text-sm font-medium text-secondary",
                  headerTitleClassName,
                  header?.className,
                )}
              >
                {/* Custom header render function */}
                {header.renderHeader ? (
                  header.renderHeader()
                ) : (
                  <>
                    <span className="truncate">{header.name}</span>

                    {header.sortable && (
                      <button
                        onClick={() => onSort?.(header.id)}
                        className={clsx(
                          "opacity-0 group-hover:opacity-100 ml-1 transition-opacity",
                          orderBy === header.id && "opacity-100",
                        )}
                      >
                        <CustomTooltip text="Sort">
                          <div
                            className={clsx(
                              "transition-transform",
                              orderBy === header.id &&
                                orderDir === sortOrder.DESC &&
                                "rotate-180",
                            )}
                          >
                            <SortIcon />
                          </div>
                        </CustomTooltip>
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Rows ===== */}
      <div className={clsx("px-6", padding)}>
        {/* ✅ Render children if present */}
        {React.Children.count(children) > 0 ? (
          children
        ) : !isLoading ? (
          <div className="w-full flex items-center justify-center h-[52px] bg-white">
            <span className="text-sm text-secondary">{emptyText}</span>
          </div>
        ) : (
          <div className="w-full flex items-center justify-center h-[52px] bg-white">
            <CircularLoader size="md" />
          </div>
        )}

        {/* ✅ Infinite scroll loader (appears at bottom) */}
        {isLoading && React.Children.count(children) > 0 && (
          <div className="w-full flex items-center justify-center py-3 bg-white">
            <CircularLoader size="sm" />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CommonGridTable);
